// useRealtimeVoice — Voice V2 real conversation mode (WebRTC).
// Connection logic ported from the proven VoiceV2Test.tsx spike (works on
// device) — same token fetch, RTCPeerConnection setup, GA event names, and
// playback hardening. This hook adds the production state machine on top:
// idle → connecting → live → (ending) → idle, plus a terminal error state,
// a 10-minute session cap, the v1 two-note chime pattern, a screen wake
// lock while live, transcript capture into the chat thread, and closing
// out the voice_sessions row on every exit path.

import { useState, useRef, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "./useAuth";
import { supabase } from "@/integrations/supabase/client";
import { EXTERNAL_SUPABASE_URL } from "@/integrations/external-supabase/client";
import {
  INITIAL_SEARCH_CAP,
  SEARCH_CAP_TOOL_RESULT,
  SEARCH_CLIENT_TIMEOUT_MS,
  SEARCH_TIMEOUT_TOOL_RESULT,
  decideVoiceSearch,
  type VoiceSearchCapState,
} from "@/lib/voiceSearchCap";
import { formatSearchLinksForChatWithCount } from "@/lib/voiceSearchLinks";
import { decideVoiceSearchOutcome } from "@/lib/voiceSearchOutcome";
import { captureReminder } from "@/lib/captureReminder";
import { saveVoiceTodo } from "@/lib/saveVoiceTodo";
import { rememberFamilyFact, undoFamilyFact } from "@/lib/rememberFamilyFact";
import { createVoiceEvent } from "@/lib/voiceCreateEvent";
import { readVoiceSchedule } from "@/lib/voiceScheduleRead";
import { readVoiceBudget } from "@/lib/voiceBudgetRead";
import { isVoiceScheduleRange } from "@/lib/voiceSchedule";
import { naturalPhrase } from "@/lib/familyFactPhrase";
import {
  INITIAL_VOICE_CAPTURE_GATE_STATE,
  decideVoiceWrite,
  onPropose,
  onUserTurn,
  type VoiceCaptureGateState,
  type VoiceWriteTool,
} from "@/lib/voiceCaptureGate";
import { mintProposeId } from "@/lib/voiceWriteBudget";
import { sendVoiceSessionCloseSnapshot } from "@/lib/voiceSessionClose";
import { createVoiceScreenWake, type VoiceScreenWake } from "@/lib/voiceScreenWake";
import { reportError } from "@/lib/reportError";
import {
  computeDailyCapWarningDelayMs,
  computeVoiceSessionLimit,
} from "@/lib/voiceSessionLimit";

const SESSION_URL = `${EXTERNAL_SUPABASE_URL}/functions/v1/kin-voice-session`;
const SEARCH_URL = `${EXTERNAL_SUPABASE_URL}/functions/v1/kin-search`;
const MAX_SESSION_MS = 10 * 60 * 1000; // 10 minutes — server-side expires_after backstops this

// What she hears about instead of a dropped call, per limit. The daily one
// matches kin-voice-session's 429 copy so hitting the wall at connection time
// and hitting it mid-sentence sound like the same Kin.
const PER_CALL_LIMIT_MESSAGE =
  "Voice sessions cap at 10 minutes — I'm still right here in text! 💜";
const DAILY_CAP_LIMIT_MESSAGE =
  "That's your 20 voice minutes for today — I'm still right here in text! Voice resets tomorrow. 💜";
// The beat between "ending" appearing and the call actually closing.
const EXIT_DELAY_MS = 500;

// The fallback shown when a connect dies somewhere we can't name. Every
// failure used to land here — see MICROPHONE_DENIED_MESSAGE below.
const GENERIC_ERROR_MESSAGE = "Voice hiccupped — try again, or type instead 💜";

// ── Microphone failures, told apart ─────────────────────────────────────────
//
// MEASURED, Sep 2 2026: four connects on one account, all of which minted a
// token successfully (the voice_sessions rows prove it — kin-voice-session
// only inserts after tokenResponse.ok) and then died in the WebRTC leg. The
// timings say where: 2.9s, then 81ms, 64ms, 431ms measured from mint to
// teardown. 64ms cannot have reached api.openai.com/v1/realtime/calls — a
// first-hit TLS handshake alone is longer than that — so those attempts died
// BEFORE the SDP exchange, and getUserMedia is the only thing there that can
// reject. One slow attempt then instant failures forever after is the iOS
// permission signature: the prompt was shown once, declined, and every later
// call rejects with NotAllowedError without prompting again.
//
// She was shown "Voice hiccupped" for a Settings toggle she could have flipped
// in ten seconds. useVoiceInput.ts — the one-shot mic in the same input bar —
// has always named this correctly; voice v2 just never got the same treatment.
//
// ⚠️ Two messages, not one. "Turn it on in Settings" is actively wrong for a
// missing device or a mic another app is holding, and a confidently wrong
// instruction is worse than an honest vague one. NotAllowedError (and
// SecurityError, which some WebKit builds use for the same refusal) is the
// only case where we know a permission is what's missing.
const MICROPHONE_DENIED_MESSAGE =
  "Kin needs microphone access — turn it on in Settings. Type instead any time 💜";
const MICROPHONE_UNAVAILABLE_MESSAGE =
  "Can't reach your microphone — type instead any time 💜";

// The 30-second heads-up before a daily-cap cutoff. SPOKEN, not just shown:
// she's on a voice call, which is the one time her eyes are least likely to be
// on the screen. The toast is the fallback for a muted phone, not the plan.
// Told to Kin as an instruction rather than injected as audio so it arrives in
// her own voice mid-conversation instead of sounding like a system barge-in.
const DAILY_CAP_WARNING_INSTRUCTIONS =
  "You have about thirty seconds of voice time left today. Right now, briefly " +
  "and warmly, tell her that — and that you're still right here in text after. " +
  "One or two sentences, then stop. Don't apologize or make it a big deal.";
const DAILY_CAP_WARNING_TOAST =
  "About 30 seconds of voice left for today 💜";

export type RealtimeVoiceState = "idle" | "connecting" | "live" | "ending" | "error";

// Two-note Web Audio chime — ported verbatim from useVoiceMode.ts's playChime
// (that file is gone as of B2 — v1's conversation loop is retired). Kept as
// a local copy rather than a shared import so this file has no v1 dependency.
// Open: ascending E5→A5. Close: descending A5→E5.
// Resumes the context if it starts suspended — a freshly created AudioContext
// right after other audio activity tears down (mic tracks stopped, pc
// closed) can come back suspended on iOS/WKWebView, especially outside a
// direct user-gesture call stack (timer expiry, error paths). Without this,
// the close chime schedules fine but never audibly plays (July 9 2026 fix —
// exitSession also now calls this BEFORE teardown, while the session's
// audio activity is still warm, for the same reason).
async function playChime(type: "open" | "close"): Promise<void> {
  try {
    const AudioContextClass = window.AudioContext ?? (window as any).webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    if (ctx.state === "suspended") {
      await ctx.resume().catch(() => {});
    }
    const notes = type === "open"
      ? [{ freq: 659, dur: 0.12 }, { freq: 880, dur: 0.18 }]
      : [{ freq: 880, dur: 0.12 }, { freq: 659, dur: 0.18 }];
    // Close chime reported quiet on device (July 9 2026) — turned up relative
    // to open, which wasn't flagged.
    const peakGain = type === "open" ? 0.3 : 0.5;
    let t = ctx.currentTime + 0.01;
    for (const { freq, dur } of notes) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(peakGain, t + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
      osc.start(t);
      osc.stop(t + dur);
      t += dur;
    }
    const totalMs = notes.reduce((s, n) => s + n.dur, 0) * 1000 + 50;
    await new Promise<void>((resolve) => {
      setTimeout(() => { try { ctx.close(); } catch (_) {} resolve(); }, totalMs);
    });
  } catch (_) {
    // Web Audio unavailable or resume failed — silent no-op, never fatal
  }
}

export interface UseRealtimeVoiceOptions {
  /** Fired once per completed utterance — her words as "user", Kin's as
   *  "assistant" — so the caller can append + persist through its own
   *  existing chat-save path. This hook does not write chat messages itself. */
  onTranscript?: (role: "user" | "assistant", content: string) => void;
}

export interface UseRealtimeVoiceReturn {
  state: RealtimeVoiceState;
  isActive: boolean;
  sessionId: string | null;
  errorStage: string | null;
  errorMessage: string | null;
  connect: () => void;
  end: () => void;
  toggle: () => void;
}

export function useRealtimeVoice(options?: UseRealtimeVoiceOptions): UseRealtimeVoiceReturn {
  const { session } = useAuth();
  const sessionRef = useRef(session);
  useEffect(() => { sessionRef.current = session; }, [session]);

  const onTranscript = options?.onTranscript;
  const onTranscriptRef = useRef(onTranscript);
  useEffect(() => { onTranscriptRef.current = onTranscript; }, [onTranscript]);

  const [state, setState] = useState<RealtimeVoiceState>("idle");
  const stateRef = useRef<RealtimeVoiceState>("idle");
  const updateState = useCallback((s: RealtimeVoiceState) => {
    stateRef.current = s;
    setState(s);
  }, []);

  const [sessionId, setSessionId] = useState<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const updateSessionId = useCallback((id: string | null) => {
    sessionIdRef.current = id;
    setSessionId(id);
  }, []);
  // Anchors duration_seconds to the same moment the server's voice_sessions
  // row set started_at (mint success), not to when WebRTC actually goes
  // live, so duration stays consistent with started_at/ended_at.
  const sessionStartRef = useRef<number | null>(null);
  // Seconds of the 20 min/day cap left as of the mint, straight from
  // kin-voice-session (null = admin = no daily limit). Read once per connect,
  // consumed when the data channel opens to size the session timer. See
  // src/lib/voiceSessionLimit.ts for why this exists (issue #377).
  const remainingDailySecondsRef = useRef<number | null | undefined>(undefined);

  const [errorStage, setErrorStage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const tracksRef = useRef<MediaStreamTrack[]>([]);
  const timerRef = useRef<number | null>(null);
  const warningTimerRef = useRef<number | null>(null);
  const hasChimedLiveRef = useRef(false);
  // Screen wake handling lives in voiceScreenWake.ts (issue #1145). It was
  // inline here, re-acquiring only on `visibilitychange` — an event Median
  // never fires — so on device the lock was never taken back once iOS dropped
  // it, and the screen locked mid-conversation.
  const screenWakeRef = useRef<VoiceScreenWake | null>(null);
  if (screenWakeRef.current === null) {
    screenWakeRef.current = createVoiceScreenWake({
      isLive: () => stateRef.current === "live",
    });
  }
  // Issue #684 — must reset per session, not just per hook lifetime, so a
  // fresh connect() never inherits a stale propose/confirm pair from a call
  // that ended mid-flow.
  const captureGateRef = useRef<VoiceCaptureGateState>(INITIAL_VOICE_CAPTURE_GATE_STATE);
  const searchCapRef = useRef<VoiceSearchCapState>(INITIAL_SEARCH_CAP);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    // Same teardown as the cutoff timer. If she hangs up at 9:45 the heads-up
    // must not fire into a dead data channel — or worse, a later call.
    if (warningTimerRef.current !== null) {
      window.clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
  }, []);

  const releaseWakeLock = useCallback(() => {
    screenWakeRef.current?.release();
  }, []);

  // Feature-detected, try/catch'd, never fatal — a refused or unsupported
  // wake lock just means the screen can lock mid-conversation, same as
  // any other voice call on a browser that doesn't support it.
  const requestWakeLock = useCallback(async () => {
    await screenWakeRef.current?.acquire("connect");
  }, []);

  // Re-acquire when she comes back, and when the platform takes the lock away
  // mid-call. Covers BOTH worlds: visibilitychange/focus in a browser or PWA,
  // and Median's median_app_resumed() global on device, where the DOM events
  // never fire at all (CLAUDE.md, "Median native shell"). The sentinel's own
  // `release` event is wired inside the controller.
  useEffect(() => screenWakeRef.current?.bind(), []);

  // --- TEARDOWN-FIRST: resource cleanup (stop tracks, close data channel,
  // close pc, clear audio, release wake lock) plus a fire-and-forget close
  // of the voice_sessions row. Safe to call from anywhere (end-tap, errors,
  // unmount, timer expiry) and safe to call more than once — the v1 killer
  // bug was "worked once, never again." The session-close write never
  // blocks or throws into the caller; it's genuinely fire-and-forget.
  const teardown = useCallback(() => {
    clearTimer();
    releaseWakeLock();

    tracksRef.current.forEach((t) => {
      try { t.stop(); } catch (_) { /* already stopped */ }
    });
    tracksRef.current = [];

    if (dcRef.current) {
      try { dcRef.current.close(); } catch (_) {}
      dcRef.current = null;
    }
    if (pcRef.current) {
      try { pcRef.current.close(); } catch (_) {}
      pcRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.srcObject = null;
      audioRef.current = null;
    }

    const closingSessionId = sessionIdRef.current;
    const startedAt = sessionStartRef.current;
    sessionStartRef.current = null;
    // Stale quota from a finished call must never size the next one's timer;
    // connect() refills it from the next mint response.
    remainingDailySecondsRef.current = undefined;
    if (closingSessionId) {
      const durationSeconds = startedAt
        ? Math.max(0, Math.round((Date.now() - startedAt) / 1000))
        : 0;
      supabase
        .from("voice_sessions")
        .update({ ended_at: new Date().toISOString(), duration_seconds: durationSeconds })
        .eq("id", closingSessionId)
        .then(({ error }: { error: any }) => {
          if (error) console.error("[useRealtimeVoice] failed to close session:", error);
        });
    }
  }, [clearTimer, releaseWakeLock]);

  const teardownRef = useRef(teardown);
  useEffect(() => { teardownRef.current = teardown; }, [teardown]);

  // Resource cleanup on unmount — no chime, the component is gone.
  useEffect(() => () => teardownRef.current(), []);

  // --- ABRUPT-CLOSE RESILIENCE (issue #378) -------------------------------
  // teardown()'s session-close write above is fire-and-forget through
  // supabase-js. That is correct and PROVEN for every in-app exit path, and
  // it is deliberately NOT changed here. What it can't do is survive the
  // webview being killed mid-call (force-quitting the app, swiping it away):
  // the request never flushes and the row is stranded at ended_at = NULL,
  // which voice_seconds_used_today() counts as ZERO seconds against the
  // 20 min/day paid-minutes cap.
  //
  // So: whenever the page goes away or goes to the background with a session
  // open, snapshot ended_at/duration_seconds through a keepalive fetch, which
  // the browser flushes even as the document dies. See lib/voiceSessionClose.ts
  // for why keepalive and not navigator.sendBeacon.
  //
  // This does NOT end the call — it only persists "as of right now." The write
  // is an absolute overwrite, so if she comes back and keeps talking, the real
  // teardown() overwrites the snapshot with the true full duration when she
  // actually hangs up. The snapshot is a floor; the normal path always wins
  // when it gets to run.
  const lastSnapshotAtRef = useRef(0);
  useEffect(() => {
    const snapshot = () => {
      const now = Date.now();
      // pagehide and visibilitychange fire back-to-back on the same close —
      // one write per event burst is plenty.
      if (now - lastSnapshotAtRef.current < 1000) return;
      const sent = sendVoiceSessionCloseSnapshot({
        sessionId: sessionIdRef.current,
        // Null once teardown() has run. The helper refuses to write in that
        // case rather than stamp a 0 over an already-correct duration.
        startedAtMs: sessionStartRef.current,
        // Synchronous read — awaiting getSession() here would lose the race
        // with the page dying (and would hit the auth lock; see CLAUDE.md).
        accessToken: sessionRef.current?.access_token,
        nowMs: now,
      });
      if (sent) lastSnapshotAtRef.current = now;
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") snapshot();
    };

    // pagehide covers force-close/navigation on iOS WKWebView, where
    // beforeunload is unreliable; visibilitychange covers app-backgrounding
    // (swipe away, home button) where pagehide may never fire at all.
    window.addEventListener("pagehide", snapshot);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", snapshot);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  // Tears down resources, resets to a terminal state, and fires the close
  // chime exactly once IF the session had actually reached "live" (so
  // canceling during "connecting" — nothing was ever audibly open — stays
  // silent). Centralizes the generic error toast too.
  const exitSession = useCallback((nextState: "idle" | "error", userMessage?: string) => {
    // Chime BEFORE teardown, while mic/pc/audio are still live — a fresh
    // AudioContext created after they're torn down can come back suspended
    // on iOS and never audibly play (July 9 2026 fix).
    const shouldChime = hasChimedLiveRef.current;
    hasChimedLiveRef.current = false;
    if (shouldChime) playChime("close");
    teardownRef.current();
    updateSessionId(null);
    updateState(nextState);
    if (nextState === "error") {
      toast(userMessage ?? GENERIC_ERROR_MESSAGE);
    }
  }, [updateState, updateSessionId]);

  const exitSessionRef = useRef(exitSession);
  useEffect(() => { exitSessionRef.current = exitSession; }, [exitSession]);

  // ⚠️ `errorStage` and `errorMessage` were set here and read by NOBODY — no
  // render site, no console, no Sentry (verified across src/ Sep 2 2026). So
  // every voice failure on a real device was silent by construction: the one
  // artifact that names the failing step was computed and dropped on the
  // floor, and diagnosing the mic bug above needed a voice_sessions forensics
  // detour instead of a log line. reportError puts the tags on the scope
  // BEFORE the console.error, so the event captureConsoleIntegration
  // synthesises carries them too — see lib/reportError.ts (#1255) for why
  // that order is the whole point.
  const fail = useCallback(
    (
      stage: string,
      message: string,
      options?: { userMessage?: string; cause?: unknown },
    ) => {
      setErrorStage(stage);
      setErrorMessage(message);
      // Not "connect failed" — fail() also runs mid-session (ice-failed,
      // data-channel, openai-event, audio-playback), and a Sentry title
      // claiming a connect failure would misdirect triage on a call that had
      // been live for minutes. The stage says which it was.
      reportError(`[voice] failed at ${stage}`, options?.cause ?? message, {
        tags: { voice_stage: stage },
        // The voice_sessions row id, so a Sentry event joins straight to the
        // row whose started_at/duration_seconds bracket the failure.
        extra: { voiceSessionId: sessionIdRef.current },
      });
      exitSessionRef.current("error", options?.userMessage);
    },
    [],
  );

  const failRef = useRef(fail);
  useEffect(() => { failRef.current = fail; }, [fail]);

  // Sends a function_call_output back over the data channel then triggers a
  // new response so Kin speaks the outcome. Guarded on dc.readyState — if
  // the session tore down while the Supabase insert was in flight, the
  // capture either fully saved or didn't (independent of the WebRTC
  // teardown), but the round-trip send is a clean no-op instead of a throw.
  const sendFunctionResult = useCallback((callId: string, output: unknown) => {
    const dc = dcRef.current;
    if (!dc || dc.readyState !== "open") return;
    try {
      dc.send(JSON.stringify({
        type: "conversation.item.create",
        item: {
          type: "function_call_output",
          call_id: callId,
          output: JSON.stringify(output),
        },
      }));
      dc.send(JSON.stringify({ type: "response.create" }));
    } catch (_) {
      // Data channel closed between the readyState check and send — abandon
      // cleanly, never crash.
    }
  }, []);

  // Registers a propose_* tool call in the confirmation gate — no write, just
  // intent. Issue #684: the model must call this before the matching write tool
  // will be honored. Always acknowledges {ok: true} so the model's turn
  // continues into asking her to confirm — rejection only ever happens at the
  // write step.
  //
  // #888: takes the tool it is proposing FOR. The gate records which one, so a
  // "yes" to "want me to add that to your list?" cannot authorize a memory
  // write instead — see voiceCaptureGate.ts.
  //
  // #933: mints the propose id here, at the proposal, not at the write. That is
  // what makes it identify the PROPOSAL rather than the attempt — two attempts
  // to honor one proposal carry the same id and collapse to one row.
  const handleProposeCall = useCallback((callId: string, tool: VoiceWriteTool) => {
    captureGateRef.current = onPropose(captureGateRef.current, tool, mintProposeId());
    sendFunctionResult(callId, { ok: true });
  }, [sendFunctionResult]);

  // Executes a search_web tool call (#887).
  //
  // Two channels out of one call, deliberately. `spoken` is the ONLY thing that
  // goes back to the realtime model — it is about to be synthesized to audio and
  // a URL is unspeakable. The links never touch the model; they go into the chat
  // through the same onTranscript path voice transcripts already use, so they
  // persist and she can find them after the call ends.
  //
  // Read-only. This handler must never gain a write path: search is the one
  // voice capability that ships without the confirmation-gate architecture
  // precisely because it changes nothing (#684/#725 explain why writes cannot be
  // gated server-side here).
  //
  // Every failure path returns a normal tool result, never a session error —
  // Kin says she could not find it and the call carries on.
  const handleSearchWebCall = useCallback(async (callId: string, argsJson: string) => {
    const decision = decideVoiceSearch(searchCapRef.current);
    searchCapRef.current = decision.nextState;
    if (!decision.allowed) {
      sendFunctionResult(callId, { ok: false, error: SEARCH_CAP_TOOL_RESULT });
      return;
    }

    let query = "";
    try {
      const parsed = JSON.parse(argsJson);
      query = typeof parsed?.query === "string" ? parsed.query : "";
    } catch (_) {
      sendFunctionResult(callId, { ok: false, error: "Couldn't read that" });
      return;
    }
    if (!query.trim()) {
      sendFunctionResult(callId, { ok: false, error: "Nothing to search for" });
      return;
    }

    const token = sessionRef.current?.access_token;
    if (!token) {
      sendFunctionResult(callId, { ok: false, error: "Not signed in" });
      return;
    }

    let spoken = "";
    let links: Array<{ title: string; url: string }> = [];
    let timedOut = false;
    try {
      const resp = await fetch(SEARCH_URL, {
        method: "POST",
        signal: AbortSignal.timeout(SEARCH_CLIENT_TIMEOUT_MS),
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ query }),
      });
      const data = await resp.json().catch(() => null);
      spoken = typeof data?.spoken === "string" ? data.spoken : "";
      links = Array.isArray(data?.links) ? data.links : [];
    } catch (e) {
      // A hung request must still produce a tool result. Without one the model
      // waits forever and Kin goes silent — see SEARCH_CLIENT_TIMEOUT_MS.
      timedOut = (e as Error)?.name === "TimeoutError";
      console.warn(`[voice-search] request ${timedOut ? "timed out" : "failed"}:`, e);
    }

    // #1690 — the spoken half and the links half must agree. The decision lives
    // in decideVoiceSearchOutcome so vitest can cover every combination; this
    // function owns only the fetch and the posting.
    //
    // ⚠️ Build the links MESSAGE first and derive the count from it, not from
    // `links.length`. formatSearchLinksForChat drops hostile and malformed URLs
    // (tag-marker injection, whitespace in the URL) and returns "" when nothing
    // survives — so `links.length` can be 4 while she sees zero. Counting the
    // raw array would let Kin say "you can see four results" over an empty chat,
    // which is this issue's own defect wearing the opposite sign.
    const { message: linksMessage, count: visibleLinkCount } = formatSearchLinksForChatWithCount(query, links);

    const outcome = decideVoiceSearchOutcome({
      spoken,
      linkCount: visibleLinkCount,
      timedOut,
    });

    // The timeout copy stays owned by voiceSearchCap.ts — the decision module
    // returns a sentinel rather than duplicating the string.
    const toolResult =
      outcome.toolResult.error === "TIMED_OUT"
        ? { ...outcome.toolResult, error: SEARCH_TIMEOUT_TOOL_RESULT }
        : outcome.toolResult;

    // Post the links BEFORE handing the result back, so they are on her screen
    // by the time the model starts speaking about them. The old order spoke
    // first and posted second, which is the race that made the mismatch feel
    // instantaneous.
    if (outcome.postLinks && linksMessage) {
      onTranscriptRef.current?.("assistant", linksMessage);
    }

    // `answer` and the link COUNT cross back to the model. The URLs never do.
    sendFunctionResult(callId, toolResult);
  }, [sendFunctionResult]);

  // Executes a confirmed capture_reminder tool call: parses the arguments,
  // reuses the Brain Dump insert path (src/lib/captureReminder.ts), and
  // reports success/failure back to the model so Kin can speak it honestly.
  // On success also fires the visible-artifact toast (guardrail 13).
  //
  // Issue #684 — gated on captureGateRef first: honored only if a
  // propose_reminder fired earlier in this session AND a real user turn
  // followed it. See voiceCaptureGate.ts for why this runs client-side, not
  // on a Kin server. A rejected call writes nothing and tells the model so
  // in a normal tool result, not a session error — she just gets asked
  // again rather than the call breaking.
  const handleCaptureReminderCall = useCallback(async (callId: string, argsJson: string) => {
    const decision = decideVoiceWrite(captureGateRef.current, "capture_reminder");
    captureGateRef.current = decision.nextState;
    if (!decision.allowed) {
      sendFunctionResult(callId, { ok: false, error: "not confirmed — ask her again before calling this" });
      return;
    }

    const userId = sessionRef.current?.user?.id;
    if (!userId) {
      sendFunctionResult(callId, { ok: false, error: "Not signed in" });
      return;
    }

    let text = "";
    try {
      const parsed = JSON.parse(argsJson);
      text = typeof parsed?.text === "string" ? parsed.text : "";
    } catch (_) {
      sendFunctionResult(callId, { ok: false, error: "Couldn't read that" });
      return;
    }

    const result = await captureReminder(userId, text, "voice", decision.proposeId);
    sendFunctionResult(callId, result);

    // ⚠️ `duplicate` is ok:true but wrote nothing — the card was already there
    // from the first attempt. Toasting again would show her a second "Added to
    // your list 💜" for one reminder, which reads as Kin having saved it twice.
    if (result.ok && result.action !== "duplicate") {
      // #1154/#1159 — this used to say "Added to your list", which is the same
      // overclaim the persona had: capture_reminder writes a Tasks tab card,
      // not a list item. save_todo below is the one that touches a list.
      toast.success("Saved to your Tasks tab 💜", {
        action: {
          label: "See it",
          onClick: () => window.dispatchEvent(new CustomEvent("kin:navigate-tab", { detail: { tab: "tasks" } })),
        },
      });
    }
  }, [sendFunctionResult]);

  // save_todo — voice's second write destination (#1159). Same gate, same
  // budget, different table: a real to-do on one of her lists rather than a
  // Ready-to-Fire card. The gate is asked about "save_todo" specifically, so a
  // yes given for a to-do cannot authorize a card or a fact write.
  const handleSaveTodoCall = useCallback(async (callId: string, argsJson: string) => {
    const decision = decideVoiceWrite(captureGateRef.current, "save_todo");
    captureGateRef.current = decision.nextState;
    if (!decision.allowed) {
      sendFunctionResult(callId, { ok: false, error: "not confirmed — ask her again before calling this" });
      return;
    }

    if (!sessionRef.current?.user?.id) {
      sendFunctionResult(callId, { ok: false, error: "Not signed in" });
      return;
    }

    let item = "";
    let listName: string | null = null;
    let createList = false;
    try {
      const parsed = JSON.parse(argsJson);
      item = typeof parsed?.item === "string" ? parsed.item : "";
      listName = typeof parsed?.list_name === "string" ? parsed.list_name : null;
      createList = parsed?.create_list === true;
    } catch (_) {
      sendFunctionResult(callId, { ok: false, error: "Couldn't read that" });
      return;
    }

    const result = await saveVoiceTodo(item, {
      listName,
      createList,
      source: "voice",
      proposeId: decision.proposeId,
    });
    sendFunctionResult(callId, result);

    // Same `duplicate` reasoning as capture_reminder: ok:true, wrote nothing,
    // so a second toast would read as Kin having saved it twice.
    if (result.ok && result.action !== "duplicate") {
      toast.success(result.listName ? `Added to ${result.listName} 💜` : "Added to your to-dos 💜", {
        action: {
          label: "See it",
          onClick: () => window.dispatchEvent(new CustomEvent("kin:navigate-tab", { detail: { tab: "tasks" } })),
        },
      });
    }
  }, [sendFunctionResult]);

  // Executes a confirmed remember_fact tool call (#888 step 3) — voice's half of
  // Family Memory. Chat has learned facts on its own since Family Memory
  // shipped; voice heard everything and remembered nothing, on the surface she
  // talks most naturally.
  //
  // Gated exactly like capture_reminder, and on the SAME gate object made
  // tool-aware (#684 two-step, enforced client-side because no Kin server is in
  // the live session — see #725). The gate is asked about "remember_fact"
  // specifically: a yes to a reminder must not authorize a memory write.
  //
  // Two receipts, deliberately, because they answer different questions:
  //   - a toast with Undo, because the only moment an unwanted fact is cheap to
  //     remove is right now, while she is holding the phone. The chat chip's
  //     Undo matches on category+key and would take out sibling rows for a
  //     multi-valued key; this one holds the exact row id (rememberFamilyFact.ts).
  //   - a line in the chat thread, because the toast is gone in seconds and the
  //     call ends. Without it there is no durable record that voice wrote to her
  //     family memory at all — same reason search links go to chat (#887).
  const handleRememberFactCall = useCallback(async (callId: string, argsJson: string) => {
    const decision = decideVoiceWrite(captureGateRef.current, "remember_fact");
    captureGateRef.current = decision.nextState;
    if (!decision.allowed) {
      sendFunctionResult(callId, {
        ok: false,
        error: "not confirmed — ask her out loud first, then call this after she says yes",
      });
      return;
    }

    let parsed: any = null;
    try {
      parsed = JSON.parse(argsJson);
    } catch (_) {
      sendFunctionResult(callId, { ok: false, error: "Couldn't read that" });
      return;
    }
    if (!parsed || typeof parsed !== "object") {
      sendFunctionResult(callId, { ok: false, error: "Couldn't read that" });
      return;
    }

    const result = await rememberFamilyFact({
      category: parsed.category,
      subject: parsed.subject,
      key: parsed.key,
      value: parsed.value,
      proposeId: decision.proposeId,
    });

    if (!result.ok) {
      sendFunctionResult(callId, { ok: false, error: result.error });
      return;
    }

    // `noop` means it was already saved, verbatim. Telling the model "saved"
    // would be true but would have Kin announce news about something she
    // already knew; telling her it was already there is both honest and more
    // useful. Either way a row exists, so this is not the #545 shape.
    //
    // #933 `duplicate` is the same shape for a different reason: this exact
    // proposal was already honored, so nothing new was written. It is grouped
    // with noop everywhere below — no second receipt, no second toast — because
    // the visible outcome is identical: one fact, saved once.
    const alreadySaved = result.action === "noop" || result.action === "duplicate";

    sendFunctionResult(callId, {
      ok: true,
      result: alreadySaved
        ? "Already saved — you already knew that. Acknowledge it briefly without making it a whole thing."
        : "Saved. Confirm out loud with the specific thing you just remembered.",
    });

    const phrase = naturalPhrase({
      subject: parsed.subject == null || String(parsed.subject).trim() === ""
        ? null
        : String(parsed.subject).trim(),
      key: String(parsed.key ?? ""),
      value: String(parsed.value ?? ""),
    });

    // ⚠️ `duplicate` is excluded here but `noop` is NOT, and the difference is
    // deliberate. A noop means SHE asked once and the fact happened to already
    // be there — one request, one receipt, which is right. A duplicate means
    // this handler ran twice for a single request; a second identical line in
    // her chat thread would be the double-write made visible, which is the
    // thing #933 exists to prevent.
    if (result.action !== "duplicate") {
      onTranscriptRef.current?.("assistant", `💜 Got it, I'll remember ${phrase}.`);
    }

    if (!alreadySaved) {
      toast.success(`Got it — I'll remember ${phrase} 💜`, {
        action: result.factId
          ? {
              label: "Undo",
              onClick: () => {
                void (async () => {
                  const undone = await undoFamilyFact(result.factId!);
                  // Never claim the undo worked without reading the write —
                  // house rule 16. A silent failure here leaves a fact she
                  // believes she removed.
                  if (undone) toast.success("Okay, forgotten.");
                  else toast.error("Couldn't undo that — you can remove it in My Kin.");
                })();
              },
            }
          : undefined,
      });
    }
  }, [sendFunctionResult]);

  // get_schedule — voice's first read of her OWN data (#1686).
  //
  // Read-only, and so deliberately OUTSIDE the capture gate: there is no
  // proposal to confirm and nothing to undo. Same posture as search_web, and
  // for the same reason — the gate exists for writes.
  //
  // ⚠️ The one thing that must never happen here is a read failure being spoken
  // as an empty calendar. "You're clear today" over a calendar Kin could not
  // reach is house rule 16 aimed at her day instead of at a write: it is a
  // confirmation over something never read, and she plans around it.
  // readVoiceSchedule keeps those two answers apart; this handler just passes
  // whichever one came back.
  const handleGetScheduleCall = useCallback(async (callId: string, argsJson: string) => {
    const userId = sessionRef.current?.user?.id;
    if (!userId) {
      sendFunctionResult(callId, { ok: false, error: "Not signed in" });
      return;
    }

    let range = "today";
    try {
      const parsed = JSON.parse(argsJson);
      // An unrecognised range falls back to today rather than erroring. The
      // enum is in the tool schema, so a bad value means the model improvised —
      // and "here is today" is a useful answer to almost anything it might have
      // meant, where a refusal is useful for nothing.
      if (isVoiceScheduleRange(parsed?.range)) range = parsed.range;
    } catch (_) {
      // Same reasoning: unparseable arguments still get her today.
    }

    const outcome = await readVoiceSchedule(supabase, userId, range as never);
    sendFunctionResult(callId, outcome);
  }, [sendFunctionResult]);

  // get_budget — her money, out loud (#1932). Voice's second read, and the
  // second tool shared with chat.
  //
  // Read-only, so deliberately OUTSIDE the capture gate, same posture as
  // get_schedule and search_web: there is no proposal to confirm and nothing to
  // undo. The gate exists for writes.
  //
  // ⚠️ No entitlement check here, on purpose. kin-voice-session already returned
  // 403 "Voice v2 requires Kin Pro" before minting this session, and its gate is
  // the STRICTER of the two (isPro || (isAdmin && adminPreview) against chat's
  // isPro || isAdmin). Re-deriving "the same check as chat" in the browser would
  // loosen it, and a client-side check is not a gate anyway.
  //
  // ⚠️ The thing that must never happen is a failed read spoken as a number, or
  // as "you haven't set up a budget". An invented figure here is money she goes
  // and spends on Kin's authority — see voiceBudgetRead.ts's header. That module
  // keeps failed / truncated / genuinely-empty apart; this handler only passes
  // back whichever one came.
  const handleGetBudgetCall = useCallback(async (callId: string, argsJson: string) => {
    const userId = sessionRef.current?.user?.id;
    if (!userId) {
      sendFunctionResult(callId, { ok: false, error: "Not signed in" });
      return;
    }

    // ⚠️ A bad or missing budget_name degrades to the ALL-budgets read rather
    // than erroring, which is also the answer the tool description pushes the
    // model towards ("usually best omitted"). Matching get_schedule's lean: an
    // improvised argument still gets her a useful answer, where a refusal gets
    // her nothing.
    let requested = "";
    try {
      const parsed = JSON.parse(argsJson);
      if (typeof parsed?.budget_name === "string") requested = parsed.budget_name;
    } catch (_) {
      // Unparseable arguments still get her the whole picture.
    }

    const outcome = await readVoiceBudget(supabase, userId, requested);
    sendFunctionResult(callId, outcome);
  }, [sendFunctionResult]);

  // create_event — voice's fourth write, and the first tool shared with chat
  // (#1686). Runs the SAME executeCreateEventTool kin-chat runs, with the
  // browser client; see src/lib/voiceCreateEvent.ts for why that is a reuse
  // rather than a second write path, and for what this write does NOT have.
  //
  // Gated on the SAME gate object as the other three writes, asked about
  // "create_event" specifically: a yes she gave about a to-do must not put a row
  // on her calendar. propose_event fires first, then a real transcribed user
  // turn, then this.
  const handleCreateEventCall = useCallback(async (callId: string, argsJson: string) => {
    const decision = decideVoiceWrite(captureGateRef.current, "create_event");
    captureGateRef.current = decision.nextState;
    if (!decision.allowed) {
      sendFunctionResult(callId, {
        ok: false,
        error: "not confirmed — say back the day and time, ask her out loud, and call this only after she says yes",
      });
      return;
    }

    const userId = sessionRef.current?.user?.id;
    if (!userId) {
      sendFunctionResult(callId, { ok: false, error: "Not signed in" });
      return;
    }

    let parsed: any = null;
    try {
      parsed = JSON.parse(argsJson);
    } catch (_) {
      sendFunctionResult(callId, { ok: false, error: "Couldn't read that" });
      return;
    }

    // #1694 — the propose id the gate minted travels with the write, so one
    // confirmation can only ever produce one row (partial unique index on
    // (user_id, propose_id)). `decision.proposeId` is already what the other three
    // voice writes pass; create_event was the one that dropped it.
    const outcome = await createVoiceEvent(parsed, userId, decision.proposeId);
    sendFunctionResult(callId, { ok: outcome.ok, result: outcome.result });

    // ⚠️ Receipts fire ONLY on `wrote` — a row this call actually created.
    // `already_there` is ok:true and wrote nothing, so a toast there would show
    // her "Added to your calendar" for an event that was already on it, which
    // reads as Kin having double-booked her. Same reasoning as the `duplicate`
    // exclusions on capture_reminder, save_todo and remember_fact above.
    if (!outcome.wrote || !outcome.event) return;

    // Two receipts, for the two different questions — same split as
    // remember_fact. The toast answers "did that land?" while she is holding the
    // phone; the chat line is the durable record, because the toast is gone in
    // seconds and the call ends. Without it there is nothing afterwards to show
    // that voice touched her calendar at all.
    const when = outcome.event.isAllDay
      ? "all day"
      : outcome.event.startTime
        ? `at ${outcome.event.startTime.slice(0, 5)}`
        : "time TBD";
    onTranscriptRef.current?.(
      "assistant",
      `📅 Added to your calendar: ${outcome.event.title} — ${outcome.event.eventDate}, ${when}.`,
    );
    toast.success("Added to your calendar 💜", {
      action: {
        label: "See it",
        onClick: () => window.dispatchEvent(new CustomEvent("kin:navigate-tab", { detail: { tab: "today" } })),
      },
    });
  }, [sendFunctionResult]);

  const connect = useCallback(async () => {
    if (stateRef.current !== "idle" && stateRef.current !== "error") return;

    setErrorStage(null);
    setErrorMessage(null);
    updateState("connecting");

    try {
      if (!sessionRef.current?.access_token) {
        throw Object.assign(new Error("No auth session"), { stage: "requesting-token" });
      }

      const tokenRes = await fetch(SESSION_URL, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${sessionRef.current.access_token}`,
          "Content-Type": "application/json",
        },
      });

      if (!tokenRes.ok) {
        const errBody = await tokenRes.text();
        // The daily cap is an expected state, not a failure — surface Kin's
        // warm message (already brand-voice server-side) and go straight
        // back to idle, no error UI, no chime.
        try {
          const parsed = JSON.parse(errBody);
          if (parsed?.error === "cap") {
            toast(parsed.message || "You've used your voice minutes for today.");
            exitSessionRef.current("idle");
            return;
          }
        } catch (_) { /* not JSON — fall through to generic failure */ }
        throw Object.assign(
          new Error(`Token mint failed (${tokenRes.status}): ${errBody}`),
          { stage: "requesting-token" }
        );
      }

      const {
        ephemeralKey,
        sessionId: mintedSessionId,
        remainingDailySeconds,
      } = await tokenRes.json();
      if (!ephemeralKey) {
        throw Object.assign(new Error("No ephemeral key in response"), { stage: "requesting-token" });
      }
      updateSessionId(mintedSessionId ?? null);
      sessionStartRef.current = Date.now();
      captureGateRef.current = INITIAL_VOICE_CAPTURE_GATE_STATE;
      searchCapRef.current = INITIAL_SEARCH_CAP;
      remainingDailySecondsRef.current = remainingDailySeconds;

      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      const audio = document.createElement("audio");
      audio.autoplay = true;
      audioRef.current = audio;
      pc.ontrack = (e) => {
        audio.srcObject = e.streams[0];
        audio.setAttribute("playsinline", "true");
        audio.play().catch((err) => {
          failRef.current(
            "audio-playback",
            `Autoplay blocked: ${err.name}: ${err.message}`,
            { cause: err },
          );
        });
      };

      pc.oniceconnectionstatechange = () => {
        const iceState = pc.iceConnectionState;
        if (iceState === "failed" || iceState === "disconnected") {
          failRef.current("ice-" + iceState, `ICE connection ${iceState}`);
        }
      };

      // Its own catch, so a refused mic is named rather than swept into the
      // generic handler at the bottom of connect() — which tagged it with
      // `stateRef.current` ("connecting"), a stage that says nothing.
      let ms: MediaStream;
      try {
        // Absent entirely on an insecure origin, and in a WKWebView whose host
        // app never enabled microphone access — `navigator.mediaDevices` is
        // then undefined and the call below is a TypeError, not a rejection.
        if (!navigator.mediaDevices?.getUserMedia) {
          throw Object.assign(new Error("navigator.mediaDevices.getUserMedia is unavailable"), {
            name: "NotSupportedError",
          });
        }
        ms = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (micErr: any) {
        const name = micErr?.name || "Error";
        // NotAllowedError is a refused permission. SecurityError is the same
        // refusal in some WebKit builds. Everything else — NotFoundError (no
        // device), NotReadableError (another app holds it), NotSupportedError
        // above — is NOT a permission problem, and must not be told to go
        // change a permission.
        const denied = name === "NotAllowedError" || name === "SecurityError";
        throw Object.assign(
          new Error(micErr?.message || String(micErr)),
          {
            // Carried across, not folded into the message: the outer catch
            // formats `${err.name}: ${err.message}`, so embedding it here read
            // "Error: NotAllowedError: …" twice over — and nothing downstream
            // could key off err.name.
            name,
            stage: denied ? "microphone-denied" : "microphone-unavailable",
            userMessage: denied ? MICROPHONE_DENIED_MESSAGE : MICROPHONE_UNAVAILABLE_MESSAGE,
            cause: micErr,
          },
        );
      }
      const track = ms.getTracks()[0];
      tracksRef.current.push(track);
      pc.addTrack(track, ms);

      const dc = pc.createDataChannel("oai-events");
      dcRef.current = dc;

      dc.addEventListener("open", () => {
        // GA API requires the client to explicitly trigger a response —
        // the model doesn't speak on its own even when connected.
        dc.send(JSON.stringify({ type: "response.create" }));

        updateState("live");
        hasChimedLiveRef.current = true;
        playChime("open");
        requestWakeLock();

        // Session hard cap: the 10-minute per-call limit, or the slice of the
        // 20 min/day cap she has left, whichever is smaller (issue #377 — the
        // daily cap used to be checked only at connection time, so a call that
        // started at 19 minutes used still ran a full uncapped 10). Both
        // limits end the call the same proven way; only the message differs.
        // No 9:30 warning for this build — auto-end shows "ending" briefly
        // with a warm toast, then goes silent.
        const { limitMs, reason } = computeVoiceSessionLimit({
          maxSessionMs: MAX_SESSION_MS,
          remainingDailySeconds: remainingDailySecondsRef.current,
          elapsedSinceMintMs: sessionStartRef.current
            ? Date.now() - sessionStartRef.current
            : 0,
        });
        // The teardown lands EXIT_DELAY_MS after the timer fires, and those
        // milliseconds are real paid time billed to her day (duration_seconds
        // runs to the actual close). For the daily cap that has to come off
        // the front so the day's total lands ON 1200s and never past it. The
        // per-call limit isn't a budget, so it keeps its exact 10 minutes.
        const fireInMs =
          reason === "daily-cap" ? Math.max(0, limitMs - EXIT_DELAY_MS) : limitMs;
        timerRef.current = window.setTimeout(() => {
          updateState("ending");
          toast(
            reason === "daily-cap" ? DAILY_CAP_LIMIT_MESSAGE : PER_CALL_LIMIT_MESSAGE,
            // The daily one is the surprising one — she was mid-conversation
            // and it won't come back until tomorrow. Give her time to read it.
            reason === "daily-cap" ? { duration: 8000 } : undefined,
          );
          window.setTimeout(() => exitSessionRef.current("idle"), EXIT_DELAY_MS);
        }, fireInMs);

        // …and 30 seconds before that, Kin says so, so the call ends instead of
        // cutting out. Null when it isn't a daily-cap call, or when the call is
        // too short for a warning to be anything but a second interruption.
        const warningDelayMs = computeDailyCapWarningDelayMs({ limitMs, reason }, fireInMs);
        if (warningDelayMs !== null) {
          warningTimerRef.current = window.setTimeout(() => {
            toast(DAILY_CAP_WARNING_TOAST, { duration: 6000 });
            // Speaking is the point; the toast is the fallback. A closed
            // channel is a no-op — she still gets the cutoff message, which is
            // the part that matters.
            const liveDc = dcRef.current;
            if (!liveDc || liveDc.readyState !== "open") return;
            try {
              liveDc.send(JSON.stringify({
                type: "response.create",
                response: { instructions: DAILY_CAP_WARNING_INSTRUCTIONS },
              }));
            } catch (_) {
              // Channel closed between the check and the send. Nothing to do —
              // never let a courtesy warning throw inside a live call.
            }
          }, warningDelayMs);
        }
      });

      dc.addEventListener("message", (e) => {
        try {
          const event = JSON.parse(e.data);

          if (event.type === "error") {
            failRef.current("openai-event", JSON.stringify(event.error || event));
            return;
          }

          // Transcript events — GA names, re-verified against OpenAI's
          // current Realtime docs before coding (do not guess from memory,
          // per the July 7 beta-shape 400).
          if (event.type === "conversation.item.input_audio_transcription.completed" && event.transcript) {
            onTranscriptRef.current?.("user", event.transcript);
            // Issue #684 — a REAL committed user turn, not a guess: this event
            // only fires once the transcription is complete, so it can't be
            // spoofed by silence or a partial utterance.
            // ⚠️ #1686 — the TRANSCRIPT is passed now, not just the fact of a
            // turn. A clear "no" withdraws the outstanding proposal instead of
            // arming it, which is what makes "saying no creates nothing" a
            // property of the gate rather than a hope about the model.
            captureGateRef.current = onUserTurn(captureGateRef.current, event.transcript);
          }
          if (event.type === "response.output_audio_transcript.done" && event.transcript) {
            onTranscriptRef.current?.("assistant", event.transcript);
          }

          // Function-call round-trip (GA event names/shapes re-verified
          // against OpenAI's current Realtime docs July 20, 2026 — do not
          // guess from memory, per the July 8 beta-shape lesson). By the
          // time response.done fires, function_call output items carry
          // complete (non-streaming) arguments.
          if (event.type === "response.done") {
            const output = event.response?.output;
            if (Array.isArray(output)) {
              for (const item of output) {
                if (item?.type !== "function_call") continue;
                if (item.name === "search_web") {
                  void handleSearchWebCall(item.call_id, item.arguments);
                } else if (item.name === "propose_reminder") {
                  handleProposeCall(item.call_id, "capture_reminder");
                } else if (item.name === "capture_reminder") {
                  void handleCaptureReminderCall(item.call_id, item.arguments);
                } else if (item.name === "propose_fact") {
                  handleProposeCall(item.call_id, "remember_fact");
                } else if (item.name === "propose_todo") {
                  handleProposeCall(item.call_id, "save_todo");
                } else if (item.name === "save_todo") {
                  void handleSaveTodoCall(item.call_id, item.arguments);
                } else if (item.name === "remember_fact") {
                  void handleRememberFactCall(item.call_id, item.arguments);
                } else if (item.name === "get_schedule") {
                  void handleGetScheduleCall(item.call_id, item.arguments);
                } else if (item.name === "get_budget") {
                  void handleGetBudgetCall(item.call_id, item.arguments);
                } else if (item.name === "propose_event") {
                  handleProposeCall(item.call_id, "create_event");
                } else if (item.name === "create_event") {
                  void handleCreateEventCall(item.call_id, item.arguments);
                }
              }
            }
          }
        } catch (_) {
          // Non-JSON message, ignore
        }
      });

      dc.addEventListener("error", (e) => {
        failRef.current("data-channel", `Data channel error: ${String(e)}`);
      });

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const sdpResponse = await fetch("https://api.openai.com/v1/realtime/calls", {
        method: "POST",
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${ephemeralKey}`,
          "Content-Type": "application/sdp",
        },
      });

      if (!sdpResponse.ok) {
        const errText = await sdpResponse.text();
        throw Object.assign(
          new Error(`SDP handshake failed (${sdpResponse.status}): ${errText}`),
          { stage: "sdp-handshake" }
        );
      }

      const answerSdp = await sdpResponse.text();
      await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
    } catch (err: any) {
      failRef.current(
        err.stage || stateRef.current,
        `${err.name || "Error"}: ${err.message || String(err)}`,
        // Undefined for every stage that has no specific copy, which falls
        // back to GENERIC_ERROR_MESSAGE — unchanged behaviour for those.
        { userMessage: err.userMessage, cause: err.cause ?? err },
      );
    }
  }, [updateState, updateSessionId, requestWakeLock]);

  const end = useCallback(() => {
    if (stateRef.current === "idle") return;
    exitSessionRef.current("idle");
  }, []);

  const toggle = useCallback(() => {
    if (stateRef.current === "idle" || stateRef.current === "error") {
      connect();
    } else {
      end();
    }
  }, [connect, end]);

  return {
    state,
    isActive: state !== "idle",
    sessionId,
    errorStage,
    errorMessage,
    connect,
    end,
    toggle,
  };
}
